<template>
  <div class="risk-template-builder d-flex flex-row">
    <feather-icon
      v-show="localItems.length > 10"
      icon="PlusCircleIcon"
      size="20"
      class="add-icon align-self-end mb-5 mr-1 cursor-pointer"
      @click="addNewQuestion"
      v-b-tooltip.hover.button.viewport="`${$t('operational_security.template.add_field_here')}`"
    />
    <div class="w-100">
      <div class="d-flex align-items-center header">
        <div class="flex-fill header-question">
          {{ $t("risk.template.question") }}
          <span class="count-pill mr-2">
            ({{ localItems.length }})
          </span>
        </div>
        <div class="section-header d-flex align-items-center">
          <b-button
            size="sm"
            variant="primary"
            title="Agregar subsección"
            v-b-tooltip.hover.button.viewport="`${$t('operational_security.template.add_field_here')}`"
            @click="addNewQuestion">
            +
          </b-button>
        </div>
      </div>

      <draggable
        v-model="localItems"
        handle=".answer-handle"
        :group="draggableGroupConfig"
        @update="emitChange"
        class="d-flex flex-column"
        item-key="item_id">
        <div v-for="(item, index) in localItems" :key="item.item_id">
          <TemplateField
            :key="item.item_id"
            :data="item"
            :selected="itemSelected && itemSelected.item_id === item.item_id"
            :select-mode="multiSelect"
            :is-selected="selectedIds.has(item.item_id)"
            @item-selected="onItemSelected"
            @show-modal="showModal"
            @remove="removeField(index)"
            @clone="cloneField(index)"
            :section-fields="linearFieldsOfSection(item)"
            :index-in-section="linearIndexOf(item)"
          />
        </div>
        <div class="d-flex justify-content-end mt-1" v-if="localItems.length > 10">
          <button @click="$emit('save-changes')" class="btn btn-success">
            Guardar cambios
          </button>
        </div>
      </draggable>
    </div>

    <template-field-types-modal
      :key="itemSelected && itemSelected.item_id"
      :type="itemSelected ? itemSelected.type : ''"
      :field-type-cfg="itemSelected ? itemSelected.options.fieldTypeCfg : { data: [] }"
      @update:type="onUpdateFieldType"
      @update:fieldTypeCfg="onUpdateFieldTypeCfg"
      @modal-close="onModalClose"
    />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { v4 as uuid } from "uuid";
import { BButton } from "bootstrap-vue";
import TemplateField from "./TemplateField.vue";
import TemplateFieldTypesModal from "./TemplateFieldTypesModal.vue";
import SectionHeader from "./SectionHeader.vue";
import SubsectionHeader from "./SubsectionHeader.vue";
import { fieldStructure } from "./config";

export default {
  props: {
    items: { type: Array, default: () => [] },
    isValid: Boolean,
  },
  components: {
    draggable,
    TemplateField,
    TemplateFieldTypesModal,
    SectionHeader,
    SubsectionHeader,
    BButton,
  },
  data() {
    return {
      sections: [],
      unsectioned: [],
      itemSelected: null,
      _isBuildingFromProps: false,
      _lastFlatSignature: "",
      multiSelect: false,
      selectedIds: new Set(),
      draggableGroupConfig: { name: 'localItems', pull: true, put: true },
      localItems: this.items ? [...this.items] : []
    };
  },
  mounted() {
  },
  watch: {
    items: {
      deep: true,
      handler(val) {
        if(this.localItems.length) return;
        this.localItems = [...val];
        this.validateFields();
      },
    },
  },
  methods: {
    addNewQuestion() {
      const f = this.newField();
      this.itemSelected = f;
      this.localItems.push(f)
      this.emitChange();
    },
    removeField(index) {
      if (!confirm(this.$t("risk.template.confirm_delete_field"))) return;
      this.localItems.splice(index, 1);
      this.emitChange();
    },
    cloneField(index) {
      const original = this.localItems[index];
      const cloned = this.cloneFieldData(original);
      this.localItems.splice(index + 1, 0, cloned);
      this.emitChange();
    },
    cloneFieldData(original) {
      const cloned = JSON.parse(JSON.stringify(original));
      cloned.item_id = uuid();
      const baseName = original.name || "";
      cloned.name = this.generateCopyName(baseName);
      cloned.errors = {};
      cloned.hasErrors = false;
      this.onItemSelected(cloned);
      return cloned;
    },

    generateCopyName(name) {
      if (!name) return "copy";
      // Si ya termina con "copy" o "copy N", incrementar el número
      const copyMatch = name.match(/^(.+?)\s*copy\s*(\d*)$/i);

      if (copyMatch) {
        const basePart = copyMatch[1].trim();
        const num = copyMatch[2] ? parseInt(copyMatch[2]) : 1;
        return `${basePart} copy ${num + 1}`;
      }

      // Si no tiene "copy", añadirlo
      return `${name} copy`;
    },

    newField() {
      const f = JSON.parse(JSON.stringify(fieldStructure));
      f.item_id = uuid();
      return f;
    },
    // visibilidad/validación utilidades
    linearFieldsOfSection(field) {
      return this.localItems.filter(item => item.id !== field.item_id);
    },
    linearIndexOf(item) {
      const arr = this.linearFieldsOfSection(item);
      return Math.max(0, this.localItems.findIndex(f => f.item_id === item.item_id));
    },
    //Sync con padre
    emitChange() {
      this.$emit("update:items", this.localItems);
      this.validateFields();
    },

    //Modal
    onUpdateFieldType(val) {
      if (!this.itemSelected) return;
      this.itemSelected.type = val;
      this.setDefaultNameOfQuestion(val);
      this.emitChange();
    },
    setDefaultNameOfQuestion(val) {
      const nameMap = {
        mail: "Email",
        relatedForms: "Formularios relacionados",
        recipient: "Destinatario",
        mailList: "Lista de correos",
        region: "Región",
        folio: "Folio",
      };
      if (nameMap[val]) {
        this.$set(this.itemSelected, "name", nameMap[val]);
      }
    },
    onUpdateFieldTypeCfg(cfg) {
      if (!this.itemSelected) return;
      if (!this.itemSelected.options) this.$set(this.itemSelected, "options", {});
      this.$set(this.itemSelected.options, "fieldTypeCfg", cfg || { data: [] });
      this.emitChange();
    },
    onItemSelected(item) {
      this.itemSelected = item;
      this.updateItemName(item.item_id, item.name);
      this.emitChange();
    },
    updateItemName(item_id, newName) {
      const item = this.localItems.find((i) => i.item_id === item_id);
      if (item) {
        item.name = newName;
      }
    },
    showModal(item) {
      this.itemSelected = item;
      this.$nextTick(() =>
        this.$root.$emit("bv::show::modal", "template_field_types_modal")
      );
    },
    onModalClose() {
      this.itemSelected = null;
    },

    //Validation
    validateFields() {
      let invalid = false;

      (this.localItems || []).forEach((item, index) => {
        if (!item.name || !item.name.trim()) invalid = true;

        const linear = this.linearFieldsOfSection(item);
        const idToIndex = new Map(linear.map((f, idx) => [f.item_id, idx]));

        const validateOne = (field, idx) => {
          field.errors = field.errors || {};
          field.hasErrors = !!field.hasErrors;
          field.errors.missingName = !field.name || field.name === "";
          field.errors.missingType = !field.type || field.type === "";

          let invalidConditions = false;
          let invalidOrder = false;
          if (Array.isArray(field.show_if)) {
            field.show_if.forEach((c) => {
              if (!c || !c.field) return;
              const refIdx = idToIndex.has(c.field) ? idToIndex.get(c.field) : -1;
              if (refIdx === -1) invalidConditions = true;
              else if (refIdx >= idx) invalidOrder = true;
            });
          }
          field.errors.invalidConditions = invalidConditions;
          field.errors.invalidOrder = invalidOrder;

          field.errors.hasErrors =
            field.errors.missingName ||
            field.errors.missingType ||
            field.errors.invalidConditions ||
            field.errors.invalidOrder;

          if (field.errors.hasErrors) invalid = true;
        };
        validateOne(item, index)
      });

      this.$emit("update:isValid", !invalid);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables/_variables.scss";

.risk-template-builder {
  background-color: #fff;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  .dark-layout & {
    border-color: $theme-dark-border-color;
    background-color: $theme-dark-card-bg;
  }

  .header {
    height: 40px;
    padding: 0 10px;
    background-color: #f4f4f4;
    border-bottom: 1px solid #d1d1d1;
    border-radius: 6px 6px 0 0;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    .dark-layout & {
      background-color: $second-dark;
      border-color: $theme-dark-border-color;
      color: $theme-dark-body-color;
    }

    &-question {
      font-weight: 600;

      .dark-layout & {
        color: $theme-dark-headings-color;
      }
    }

    &-type-response {
      padding-left: 10px;
      width: calc(310px - 1px);
      height: 100%;
      line-height: 33px;
      border-left: 1px solid #d1d1d1;

      .dark-layout & {
        border-left-color: $theme-dark-border-color;
      }
    }
  }
}

.section-inner {
  padding-left: 16px;

  .dark-layout & {
    background-color: transparent;
  }
}

.subsection-inner {
  padding-left: 12px;

  .dark-layout & {
    background-color: transparent;
  }
}

.section-box {
  border: 1px dashed #ddd;
  padding: 0.5rem;
  border-radius: 6px;
  background: #fff;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  .dark-layout & {
    background: $theme-dark-input-bg;
    border-color: $theme-dark-input-border-color;
  }
}

.section-separator {
  border-top: 1px solid #e6e6e6;
  margin: 12px 0 16px;

  .dark-layout & {
    border-top-color: $theme-dark-border-color;
  }
}

.unsectioned {
  border-top: 1px solid #eee;
  padding-top: 0.75rem;

  .dark-layout & {
    border-top-color: $theme-dark-border-color;
  }

  strong {
    .dark-layout & {
      color: $theme-dark-headings-color;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
